/**
 * EntryTypeToggle — compact "Type of Entry" segmented control that floats
 * at the top-left of the map (June 2026 filter-bar slim-down). Pulled out
 * of the Display Settings drawer so it is always visible, but kept small
 * and separate from the filter strip's category chips.
 *
 * Three mutually-exclusive lenses, mapped onto the existing entryTypes Set
 * (so the backend `entry_types` CSV contract is unchanged):
 *   - All        -> { personal, verified, unverified }  (no filter)
 *   - Histories  -> { verified, unverified }
 *   - Memories   -> { personal }
 *
 * State lives in FilterContext; this is pure presentation + dispatch.
 */

import React from "react";
import { useFilter } from "./FilterContext";
import config from "./config";
import "./EntryTypeToggle.css";

const SEGMENTS = [
  { key: "all", label: "All", glyph: "mixed", values: ["personal", "verified", "unverified"] },
  { key: "histories", label: "Histories", glyph: "history", values: ["verified", "unverified"] },
  { key: "memories", label: "Memories", glyph: "memory", values: ["personal"] },
];

function EntryGlyph({ kind }) {
  return (
    <span className={`entry-toggle__glyph entry-toggle__glyph--${kind}`} aria-hidden="true">
      {(kind === "history" || kind === "mixed") && (
        <span className="entry-toggle__glyph-shape entry-toggle__glyph-shape--history" />
      )}
      {(kind === "memory" || kind === "mixed") && (
        <span className="entry-toggle__glyph-shape entry-toggle__glyph-shape--memory" />
      )}
    </span>
  );
}

export default function EntryTypeToggle() {
  const { entryTypes, setEntryTypes } = useFilter();
  const buttonRefs = React.useRef([]);
  const set = entryTypes instanceof Set ? entryTypes : new Set();
  const size = set.size;

  // Derive which of the three segments is active from the Set contents.
  let active = null;
  if (size >= 3) active = "all";
  else if (size === 2 && set.has("verified") && set.has("unverified"))
    active = "histories";
  else if (size === 1 && set.has("personal")) active = "memories";

  const selectAt = (index) => {
    const normalized = (index + SEGMENTS.length) % SEGMENTS.length;
    setEntryTypes(SEGMENTS[normalized].values);
    buttonRefs.current[normalized]?.focus();
  };

  const handleKeyDown = (event, index) => {
    if (["ArrowRight", "ArrowDown"].includes(event.key)) {
      event.preventDefault();
      selectAt(index + 1);
    } else if (["ArrowLeft", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      selectAt(index - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      selectAt(0);
    } else if (event.key === "End") {
      event.preventDefault();
      selectAt(SEGMENTS.length - 1);
    }
  };

  return (
    <div className="entry-toggle" role="radiogroup" aria-label="Type of entry">
      {SEGMENTS.map((seg, index) => {
        const isActive = active === seg.key;
        return (
          <button
            key={seg.key}
            ref={(element) => { buttonRefs.current[index] = element; }}
            type="button"
            role="radio"
            aria-checked={isActive}
            tabIndex={isActive || (active === null && index === 0) ? 0 : -1}
            className={[
              "entry-toggle__segment",
              isActive ? "entry-toggle__segment--active" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => setEntryTypes(seg.values)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {config.ENTRY_TYPE_SHAPES_ENABLED && <EntryGlyph kind={seg.glyph} />}
            <span>{seg.label}</span>
          </button>
        );
      })}
    </div>
  );
}
